import { isDevicesTree, RoutePath } from "src/data/Routes";
import { MenuNode } from "src/model/MenuNode";
import { Log } from "src/service/log.service";
import { GLOB } from "src/util/Glob";
import { commitSelection, Nav, NavContext, NavResult } from "./NavDomain";

/** Groups can be nested multilevel, so search recursively. */
function findGroupNode(nodes: MenuNode[], group: string): MenuNode {
  for (const node of nodes) {
    if (MenuNode.getGroupPathSegment(node) === group) {
      return node;
    }
    if (node.children) {
      const found = findGroupNode(node.children, group);
      if (found) return found;
    }
  }
}

export class StorageRoot {
  cls: MenuNode;
  nodes: MenuNode[];
  group: string;
  groupPath = '';
  groupNode: MenuNode;
  /** Redirect when the requested group does not exist. */
  nav: Nav;
}

/** Swaps the class root for the storages-by device/group/type tree the URL (or current selection) points into. */
export function resolveStorageRoot(cls: MenuNode, ctx: NavContext): StorageRoot {
  const { clsPath, allType, groupName } = ctx;
  const res = new StorageRoot();
  const keepCls = GLOB.selectedClass === clsPath && GLOB.selectedAllType === allType;
  switch (allType || GLOB.selectedAllType) {
    case RoutePath.DEVICE:
      res.cls = keepCls ? cls : GLOB.menuService.storagesBy.device;
      break;
    case RoutePath.GROUP:
      res.cls = keepCls ? cls : GLOB.menuService.storagesBy.group;
      res.group = allType ? groupName : GLOB.selectedGroupId;
      if (res.group) {
        res.groupPath = '/' + res.group;
        res.groupNode = findGroupNode(res.cls.children, res.group);
        if (!res.groupNode) {
          Log.warn(`No group "${res.group}" found.`);
          res.nav = { to: [clsPath, allType].join('') };
          return res;
        }
        res.nodes = res.groupNode.children;
      } else {
        res.nodes = res.cls.children;
      }
      break;
    case RoutePath.TYPE:
      res.cls = keepCls ? cls : GLOB.menuService.storagesBy.type;
      break;
    default:
      res.cls = cls;
      Log.error('Unknown type ' + (allType || GLOB.selectedAllType));
  }
  const storages = GLOB.menuService.storagesBy;
  switch (allType) {
    case RoutePath.DEVICE:
      res.cls.title = storages.device.title;
      break;
    case RoutePath.GROUP:
      res.cls.title = storages.group.title;
      break;
    case RoutePath.TYPE:
      res.cls.title = storages.type.title;
      break;
  }
  return res;
}

/**
 * Devices/groups tree with a type segment not matching the loaded tree
 */
export function resolveDeviceTreeItem(ctx: NavContext, cls: MenuNode, typ: MenuNode, nodes: MenuNode[],
  group: string, groupPath: string): { typ: MenuNode; result?: NavResult; } {
  const { clsPath, allType, typeOrDevice, typeOrDevicePath, itemSearch, itemHash, location } = ctx;
  if (GLOB.selectedClass !== clsPath || !isDevicesTree(GLOB.selectedAllType)
    || typ?.title === typeOrDevice || !nodes?.length) {
    return null;
  }
  typ = { key: 'dummyType', children: typeOrDevice ? nodes.filter(mn => mn.hwType === typeOrDevice) : typ.children };
  if (!typ.children?.length) {
    typ.children = [];
    for (const grup of nodes) {
      const types = grup.children?.filter(child => child.hwType === typeOrDevice);
      if (types?.length) {
        for (const t of types) {
          t.parent = grup;
        }
        typ.children.push(...types);
      }
    }
  }
  const item = MenuNode.findDeepNode(typ.children, itemSearch, typeOrDevicePath + '/', group);
  if (!item) {
    return { typ };
  }
  const result = new NavResult();
  MenuNode.mapParent(cls.children, cls);
  const device = MenuNode.getHwTypeParent(item);
  const devicePath = device ? allType === RoutePath.TYPE ? device.path : ('/' + device.titleText) : '';
  const path = clsPath + allType + groupPath + devicePath + itemSearch;
  if (path !== (decodeURIComponent(location.pathname + location.search))) {
    result.nav = { to: path + itemHash, options: { replace: true } };
    return { typ, result };
  }
  commitSelection(clsPath, allType, group, typeOrDevicePath);
  result.item = GLOB.selectedItem = item;
  result.tree = cls;
  return { typ, result };
}
